import React, { useCallback, useEffect, useState } from 'react';
import { PersonDetail, PersonIdentifier, PersonLinkSuggestion, PersonSummary } from '../types';
import { peopleService, PersonAction } from '../services/api';
import { formatDateTime } from '../utils/datetime';
import ScopeFilter, { ScopeSelection } from './ScopeFilter';

// People: one row per human (or shared line/mailbox), with every address it
// answers to on any channel — the person-identity plan's phase 4.
//
// What an administrator can do here, decided 10 Oct 2026: merge two records
// that are one person, split an address back out, mark an address shared (a
// main line, support@), and set what kind of party a record is. Every action
// needs a written reason and lands in the person ledger. Phone numbers that
// appear on two records are SUGGESTED, never linked automatically — a matching
// number is the commonest coincidence and the commonest reassignment.

const SCHEME_LABELS: Record<string, string> = {
  ns_uid: 'Seat', sip: 'SIP', tel: 'Phone', mailto: 'Email', matrix: 'Chat',
  wa_jid: 'WhatsApp', wa_lid: 'WhatsApp (LID)', fb: 'Messenger', gchat: 'Google Chat',
};

const ASSURANCE: Record<number, { label: string; title: string }> = {
  0: { label: 'seen', title: 'Observed in a header or caller ID; nothing vouches for it' },
  1: { label: 'attested', title: 'Vouched for by our own system: the PBX named this seat, or the homeserver this login' },
  2: { label: 'proven', title: 'The person proved control: Horizon sign-in, or a one-time code sent to it' },
  3: { label: 'admin', title: 'An administrator attested it, with a reason' },
};

const PURPOSES = ['recording', 'transcription', 'analysis', 'ai_training'] as const;

const errorOf = (e: any, fallback: string) => e?.response?.data?.error || fallback;
const nameOf = (p: { display_name: string | null; person_id: string }) =>
  p.display_name || `(unnamed ${p.person_id.slice(0, 8)})`;

const AssuranceBadge: React.FC<{ level: number | null }> = ({ level }) => {
  if (level === null || level === undefined) return <span className="small">—</span>;
  const a = ASSURANCE[level] || ASSURANCE[0];
  const cls = level >= 2 ? 'status status-completed' : level === 1 ? 'status status-active' : 'status';
  return <span className={cls} title={a.title}>{a.label}</span>;
};

// One dialog for every action: says what will happen, and will not proceed
// without a reason, because the reason is what the ledger keeps.
const ActionDialog: React.FC<{
  title: string;
  explain: React.ReactNode;
  confirmLabel: string;
  extra?: React.ReactNode;
  onConfirm: (reason: string) => Promise<void>;
  onClose: () => void;
}> = ({ title, explain, confirmLabel, extra, onConfirm, onClose }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (e) {
      setError(errorOf(e, 'That did not work.'));
      setBusy(false);
    }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button type="button" onClick={onClose}>Close</button>
        </div>
        <div className="small" style={{ margin: '0.6rem 0' }}>{explain}</div>
        {extra}
        <label className="small" style={{ display: 'block', marginTop: '0.6rem' }}>
          Reason (kept in the ledger)
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
            style={{ width: '100%', marginTop: '0.3rem' }} placeholder="e.g. Same customer: confirmed on the phone on 10 Oct" />
        </label>
        {error && <div className="error" style={{ marginTop: '0.5rem' }}>{error}</div>}
        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="cp-gold-btn" onClick={go} disabled={busy || reason.trim().length < 8}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

type Dialog =
  | { kind: 'merge'; survivor: PersonSummary | PersonDetail['person']; other: string; otherName: string }
  | { kind: 'split'; ident: PersonIdentifier; person: string }
  | { kind: 'shared'; ident: PersonIdentifier }
  | { kind: 'kind'; person: PersonDetail['person'] }
  | { kind: 'rename'; person: PersonDetail['person'] };

const PeoplePage: React.FC = () => {
  const [scope, setScope] = useState<ScopeSelection>({});
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState<PersonSummary[]>([]);
  const [suggestions, setSuggestions] = useState<PersonLinkSuggestion[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [detail, setDetail] = useState<PersonDetail | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [newKind, setNewKind] = useState('person');
  const [newName, setNewName] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    peopleService.list(query || undefined, scope)
      .then(r => { setPeople(r.people); setSuggestions(r.suggestions); setCanEdit(r.can_edit); setError(null); })
      .catch(e => setError(errorOf(e, 'Could not load people.')))
      .finally(() => setLoading(false));
  }, [query, scope]);

  useEffect(load, [load]);

  const openPerson = (id: string | null) => {
    setOpen(id);
    setDetail(null);
    if (id) {
      peopleService.get(id).then(setDetail).catch(e => setError(errorOf(e, 'Could not load that person.')));
    }
  };

  // After any action: reload the list and whichever person is now the one to
  // show (a merge's survivor, or the record that was split from).
  const after = (focus: string | null) => { load(); openPerson(focus); };
  const act = async (body: PersonAction, focus: string | null) => {
    const r = await peopleService.act(body);
    after(r.person_id && body.action === 'merge' ? r.person_id : focus);
  };

  const byId = Object.fromEntries(people.map(p => [p.person_id, p]));
  const suggestionsFor = (id: string) => suggestions.filter(s => s.a === id || s.b === id);

  return (
    <div className="table-wrapper">
      <div className="table-header">
        <h2>People</h2>
        <span className="small">
          {people.length} record{people.length === 1 ? '' : 's'}
          {suggestions.length > 0 && <> · {suggestions.length} suggested link{suggestions.length === 1 ? '' : 's'}</>}
        </span>
      </div>

      <p className="small" style={{ maxWidth: '58rem' }}>
        Each record is one person, or one shared line or mailbox, with every address it answers to:
        a NetSapiens seat and its devices, phone numbers, email and chat. Consent belongs to the
        record, so one decision covers every address in it. A record whose addresses only match by
        phone number is suggested here, never joined automatically.
        {!canEdit && <> Your account can look but not change; ask a territory administrator.</>}
      </p>

      <ScopeFilter value={scope} onChange={setScope} />
      <form className="search-form" onSubmit={e => { e.preventDefault(); setQuery(search.trim()); }}>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Name, number, email, SIP address…" />
        <button type="submit">Search</button>
      </form>

      {error && <div className="error">{error}</div>}
      {loading ? <div className="loading">Loading…</div> : people.length === 0 ? (
        <p className="no-data">No one matches.</p>
      ) : (
        <div className="table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th></th><th>Name</th><th>Kind</th><th>Addresses</th><th>Best proof</th>
                <th>Consent</th><th>Conversations</th><th>Scope</th>
              </tr>
            </thead>
            <tbody>
              {people.map(p => (
                <React.Fragment key={p.person_id}>
                  <tr>
                    <td>
                      <button type="button" onClick={() => openPerson(open === p.person_id ? null : p.person_id)}
                        title={open === p.person_id ? 'Collapse' : 'Show everything about this person'}>
                        {open === p.person_id ? '▾' : '▸'}
                      </button>
                    </td>
                    <td>
                      {nameOf(p)}
                      {suggestionsFor(p.person_id).length > 0 && (
                        <span className="status status-active" style={{ marginLeft: 6 }}
                          title="Another record shares a phone number with this one">
                          possible match
                        </span>
                      )}
                    </td>
                    <td className="small">{p.kind}</td>
                    <td className="small">
                      {p.identifiers.map(i => (
                        <div key={i.id} className="monospace" title={SCHEME_LABELS[i.scheme] || i.scheme}>
                          {i.value_norm}{Number(i.shared) ? ' (shared)' : ''}
                        </div>
                      ))}
                    </td>
                    <td><AssuranceBadge level={p.max_assurance} /></td>
                    <td className="small">
                      {p.consent
                        ? (PURPOSES.filter(k => p.consent![k]).join(', ') || 'none') + ` · ${p.consent.lawful_basis}`
                        : <span title="No decision on record: each conversation gets its domain's default">default</span>}
                    </td>
                    <td>{p.conversations}</td>
                    <td className="small">{p.territory}{p.domain ? ` / ${p.domain}` : ''}</td>
                  </tr>

                  {open === p.person_id && (
                    <tr>
                      <td colSpan={8} style={{ paddingLeft: '28px' }}>
                        {!detail ? <span className="small">Loading…</span> : (
                          <PersonPanel d={detail} canEdit={canEdit} byId={byId}
                            onSplit={ident => setDialog({ kind: 'split', ident, person: detail.person.person_id })}
                            onShared={ident => setDialog({ kind: 'shared', ident })}
                            onRename={() => { setNewName(detail.person.display_name || ''); setDialog({ kind: 'rename', person: detail.person }); }}
                            onKind={() => { setNewKind(detail.person.kind === 'person' ? 'role' : 'person'); setDialog({ kind: 'kind', person: detail.person }); }}
                            onMerge={(other, otherName) => setDialog({ kind: 'merge', survivor: detail.person, other, otherName })} />
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog?.kind === 'merge' && (
        <ActionDialog title="Merge into this person" confirmLabel="Merge"
          explain={<>
            <b>{dialog.otherName}</b> will be folded into <b>{nameOf(dialog.survivor)}</b>: every address,
            conversation and registry entry moves across. Consent becomes the <b>most restrictive</b> of the
            two on each purpose until the person decides again. Undo by splitting an address back out.
          </>}
          onConfirm={reason => act({ action: 'merge', survivor: dialog.survivor.person_id, absorbed: dialog.other, reason }, dialog.survivor.person_id)}
          onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'split' && (
        <ActionDialog title="Split this address out" confirmLabel="Split"
          explain={<>
            <span className="monospace">{dialog.ident.value_norm}</span> becomes a record of its own. Its
            conversations go with it, and so does the registry entry keyed on it; its consent is then that
            entry&rsquo;s, or its domain&rsquo;s default — never this person&rsquo;s.
          </>}
          onConfirm={reason => act({ action: 'split', identifier_id: dialog.ident.id, reason }, dialog.person)}
          onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'shared' && (
        <ActionDialog title={Number(dialog.ident.shared) ? 'No longer shared' : 'Mark as shared'}
          confirmLabel={Number(dialog.ident.shared) ? 'Unmark' : 'Mark shared'}
          explain={Number(dialog.ident.shared)
            ? <>This address will again carry this person&rsquo;s own consent when it meets the channel&rsquo;s bar.</>
            : <>A shared address (a main line, support@, a family phone) never carries one person&rsquo;s grant:
                its conversations get the domain default, minus anything this person refused.</>}
          onConfirm={reason => act({ action: 'set_shared', identifier_id: dialog.ident.id, shared: !Number(dialog.ident.shared), reason }, open)}
          onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'rename' && (
        <ActionDialog title="Rename" confirmLabel="Rename"
          explain={<>The name shown for this record here and in new conversations. It changes no address and no
            consent, and a conversation already kept keeps the name it was written with.</>}
          extra={
            <input type="text" value={newName} onChange={e => setNewName(e.target.value)} maxLength={255}
              style={{ width: '100%' }} aria-label="New name" autoFocus />}
          onConfirm={reason => act({ action: 'rename', person_id: dialog.person.person_id, name: newName.trim(), reason }, dialog.person.person_id)}
          onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'kind' && (
        <ActionDialog title="What kind of party is this?" confirmLabel="Save"
          explain={<>A <b>role</b> (support@, a main line) or <b>service</b> (no-reply@) is not a person: all its
            addresses become shared, and it can never be merged into a person.</>}
          extra={
            <select value={newKind} onChange={e => setNewKind(e.target.value)}>
              {['person', 'role', 'service', 'unknown'].map(k => <option key={k} value={k}>{k}</option>)}
            </select>}
          onConfirm={reason => act({ action: 'set_kind', person_id: dialog.person.person_id, kind: newKind, reason }, dialog.person.person_id)}
          onClose={() => setDialog(null)} />
      )}
    </div>
  );
};

const PersonPanel: React.FC<{
  d: PersonDetail;
  canEdit: boolean;
  byId: Record<string, PersonSummary>;
  onSplit: (i: PersonIdentifier) => void;
  onShared: (i: PersonIdentifier) => void;
  onKind: () => void;
  onRename: () => void;
  onMerge: (other: string, otherName: string) => void;
}> = ({ d, canEdit, byId, onSplit, onShared, onKind, onRename, onMerge }) => {
  const [mergeId, setMergeId] = useState('');
  const live = d.identifiers.filter(i => !i.valid_to);
  const closed = d.identifiers.filter(i => i.valid_to);
  const me = d.person.person_id;
  return (
    <div className="small">
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <span className="monospace" title="The id written into every vCon this person is party to, as the party's uuid">
          {me}
        </span>
        <span>· {d.person.status} · {d.person.kind}</span>
        {canEdit && <button type="button" onClick={onRename}>Rename</button>}
        {canEdit && <button type="button" onClick={onKind}>Change kind</button>}
      </div>

      <table className="sessions-table">
        <thead>
          <tr><th>Address</th><th>Type</th><th>Proof</th><th>How</th><th>Last seen</th><th></th></tr>
        </thead>
        <tbody>
          {live.map(i => (
            <tr key={i.id}>
              <td className="monospace">{i.value_norm}{Number(i.shared) ? ' (shared)' : ''}</td>
              <td>{SCHEME_LABELS[i.scheme] || i.scheme}</td>
              <td><AssuranceBadge level={i.assurance} /></td>
              <td>{i.proof_type || i.source || '—'}</td>
              <td>{i.last_seen_at ? formatDateTime(i.last_seen_at) : '—'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {canEdit && <>
                  <button type="button" onClick={() => onShared(i)}>{Number(i.shared) ? 'Unshare' : 'Shared'}</button>{' '}
                  <button type="button" disabled={live.length < 2} onClick={() => onSplit(i)}
                    title={live.length < 2 ? 'The only address; nothing to split from' : 'Move this address to a record of its own'}>
                    Split
                  </button>
                </>}
              </td>
            </tr>
          ))}
          {closed.map(i => (
            <tr key={i.id} style={{ opacity: 0.55 }}>
              <td className="monospace">{i.value_norm}</td>
              <td>{SCHEME_LABELS[i.scheme] || i.scheme}</td>
              <td colSpan={4}>closed {i.valid_to ? formatDateTime(i.valid_to) : ''} (reassigned)</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: '0.6rem' }}>
        <b>Consent</b>{' '}
        {d.consent.length === 0 ? <>none on record — each conversation gets its domain&rsquo;s default</> :
          d.consent.map(c => (
            <span key={c.channel} style={{ marginRight: 12 }}>
              {c.channel === '*' ? 'all channels' : c.channel}:{' '}
              {PURPOSES.filter(k => Number((c as any)['consent_' + k])).join(', ') || 'none'}
              {' '}({c.lawful_basis}{c.proof_type ? `, ${c.proof_type}` : ''})
            </span>
          ))}
        {d.registry.length > 1 && <> · from {d.registry.length} registry entries, the most restrictive of them</>}
      </div>

      {d.suggestions.length > 0 && (
        <div style={{ marginTop: '0.6rem' }}>
          <b>Possibly the same person</b>
          {d.suggestions.map(s => {
            const other = s.a === me ? s.b : s.a;
            const o = byId[other];
            const otherName = o ? nameOf(o) : other.slice(0, 8);
            return (
              <div key={other + s.key} style={{ marginTop: '0.25rem' }}>
                {otherName} — both have <span className="monospace">{s.key}</span>{' '}
                ({s.a === me ? s.b_identifier : s.a_identifier}){' '}
                {canEdit && <button type="button" onClick={() => onMerge(other, otherName)}>Merge into this person</button>}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && (
        <div style={{ marginTop: '0.6rem' }}>
          <b>Merge another record into this one</b>{' '}
          <select value={mergeId} onChange={e => setMergeId(e.target.value)}>
            <option value="">choose…</option>
            {Object.values(byId).filter(p => p.person_id !== me && !['role', 'service'].includes(p.kind)).map(p => (
              <option key={p.person_id} value={p.person_id}>
                {nameOf(p)} — {p.identifiers.map(i => i.value_norm).join(', ').slice(0, 60)}
              </option>
            ))}
          </select>{' '}
          <button type="button" disabled={!mergeId}
            onClick={() => onMerge(mergeId, byId[mergeId] ? nameOf(byId[mergeId]) : mergeId)}>Merge…</button>
        </div>
      )}

      {d.absorbed.length > 0 && (
        <div style={{ marginTop: '0.6rem' }}>
          <b>Merged into this record</b>{' '}
          {d.absorbed.map(a => <span key={a.person_id} className="monospace" style={{ marginRight: 8 }}>{a.person_id.slice(0, 8)}</span>)}
          <span>(older vCons may name these ids; they resolve here)</span>
        </div>
      )}

      <div style={{ marginTop: '0.6rem' }}>
        <b>Conversations</b> {d.conversation_count}
        {d.conversations.slice(0, 8).map(c => (
          <div key={c.uuid + c.party_index} className="monospace" style={{ opacity: 0.85 }}>
            {formatDateTime(c.created_at)} · {c.kind} · as {c.party_tel || '—'} · {c.uuid}
          </div>
        ))}
        {d.conversation_count > 8 && <div>…and {d.conversation_count - 8} more</div>}
      </div>

      {d.ledger.length > 0 && (
        <div style={{ marginTop: '0.6rem' }}>
          <b>History</b>
          {d.ledger.map(e => (
            <div key={e.id}>
              {formatDateTime(e.created_at)} · <span className="monospace">{e.event}</span> by {e.actor_name}
              {e.reason ? <> — &ldquo;{e.reason}&rdquo;</> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PeoplePage;
